import { TFunction } from 'i18next';
import { formatApiDateForExport } from '@Utils';

import { DataSupplier } from '@Models';
import { getSupplierColumns, SupplierColumnId } from './columns';
import { getConnectorLabel, getStatusLabel } from './labels';

/**
 * Builds the CSV rows for the suppliers export from the same per-flavor column
 * layout the table renders, so the file always matches what is on screen.
 *
 * Cells carry the rendered labels, not the raw internal values — a file handed
 * to someone outside the platform shouldn't say `ACCOUNT_READY` or `gs1_wrl`
 * under translated headers. The date is the exception and goes out as ISO, so a
 * spreadsheet can sort it (see `formatApiDateForExport`).
 */

// `actions` is the ⋮ menu — a control, not data, so it has no cell to export.
const NOT_EXPORTABLE: SupplierColumnId[] = ['actions'];

type CellValue = (supplier: DataSupplier, t: TFunction) => string;

const HEADERS: Record<Exclude<SupplierColumnId, 'actions'>, (t: TFunction) => string> = {
  gln: () => 'GLN',
  name: (t) => t('Supplier.Name'),
  status: (t) => t('Supplier.Status'),
  plan: (t) => t('Supplier.Plan'),
  addons: (t) => t('Supplier.AddonsColumn'),
  connectors: (t) => t('Supplier.Connectors'),
  createdAt: (t) => t('Supplier.CreatedAt'),
};

const CELLS: Record<Exclude<SupplierColumnId, 'actions'>, CellValue> = {
  gln: (supplier) => supplier.gln ?? '',
  name: (supplier) => supplier.display_name,
  status: (supplier, t) => getStatusLabel(supplier.configuration?.status ?? '', t).label,
  plan: (supplier) => supplier.plan ?? '',
  addons: (supplier) => (supplier.enabled_addons ?? []).map((addon) => addon.dashboard_name).join(', '),
  connectors: (supplier) => (supplier.connectors ?? []).map(getConnectorLabel).join(', '),
  createdAt: (supplier) => formatApiDateForExport(supplier.created_at),
};

const buildSupplierExportRows = (suppliers: DataSupplier[], t: TFunction): Array<Record<string, string>> => {
  const columns = getSupplierColumns().filter(
    (id): id is Exclude<SupplierColumnId, 'actions'> => !NOT_EXPORTABLE.includes(id),
  );

  // DownloadCsv is configured with `useKeysAsHeaders`, so the key *is* the
  // column header the user sees.
  return suppliers.map((supplier) => Object.fromEntries(columns.map((id) => [HEADERS[id](t), CELLS[id](supplier, t)])));
};

export default buildSupplierExportRows;
